import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UploadCloud, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import DatePickerField from "@/components/DatePickerField";

const emptyFrom = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""]));

/** Renders a single field row given a config entry — text/textarea/select/file/date/number. */
function FieldRow({ f, value, filenameValue, form, setForm, testidPrefix, uploading, fileRefs, uploadFile, clearFile }) {
  const onChange = (v) => setForm((s) => ({ ...s, [f.name]: v }));

  if (f.type === "textarea") {
    return (
      <Textarea data-testid={`${testidPrefix}-field-${f.name}`} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)} rows={3} />
    );
  }
  if (f.type === "select") {
    return (
      <select data-testid={`${testidPrefix}-field-${f.name}`} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
        <option value="">Select…</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "date") {
    return (
      <DatePickerField testid={`${testidPrefix}-field-${f.name}`} value={value ?? ""} onChange={onChange} />
    );
  }
  if (f.type === "file") {
    return (
      <div>
        <input ref={(el) => (fileRefs.current[f.name] = el)} type="file"
          accept={f.accept || "application/pdf,image/*"} className="hidden"
          onChange={(e) => uploadFile(f.name, e)} />
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid={`${testidPrefix}-field-${f.name}-btn`} type="button" variant="outline" size="sm"
            onClick={() => fileRefs.current[f.name]?.click()} disabled={uploading === f.name} className="h-9 gap-1.5">
            {uploading === f.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {value ? "Replace" : "Attach file"}
          </Button>
          {value && (
            <>
              <span className="text-xs text-slate-500 font-mono truncate max-w-[200px]" title={filenameValue || value}>
                {filenameValue || "file attached"}
              </span>
              <button type="button" onClick={() => clearFile(f.name)} className="text-slate-400 hover:text-rose-500">
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <Input data-testid={`${testidPrefix}-field-${f.name}`}
      type={f.type === "number" ? "number" : "text"} value={value ?? ""}
      onChange={(e) => onChange(e.target.value)} className={f.mono ? "font-mono" : ""} />
  );
}

/**
 * Modal form for creating / editing a DataManager row.
 * Split out of DataManager.js so form state, uploads, and template pre-fill live in one place.
 */
export function ItemForm({ testidPrefix, endpoint, fields, open, onOpenChange, editing, onSaved,
                          templates, templateLabel, onApplyTemplate }) {
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const fileRefs = useRef({});

  // Reset form each time the dialog opens for a new row / edit target.
  const handleOpenChange = (o) => {
    if (o) {
      setForm(editing ? { ...emptyFrom(fields), ...editing } : emptyFrom(fields));
      setTemplateId("");
    }
    onOpenChange(o);
  };

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    if (!tid) return;
    const tpl = (templates || []).find((t) => t.id === tid);
    if (!tpl || !onApplyTemplate) return;
    const patch = onApplyTemplate(tpl) || {};
    setForm((f) => ({ ...f, ...patch }));
    toast.success(`Loaded "${tpl.name}"`);
  };

  const uploadFile = async (fieldName, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(fieldName);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [fieldName]: data.path, [`${fieldName}_filename`]: data.original_filename }));
      toast.success("File attached");
    } catch { toast.error("Upload failed"); }
    finally {
      setUploading(null);
      if (fileRefs.current[fieldName]) fileRefs.current[fieldName].value = "";
    }
  };

  const clearFile = (fieldName) =>
    setForm((f) => ({ ...f, [fieldName]: "", [`${fieldName}_filename`]: "" }));

  const save = async () => {
    for (const f of fields) {
      if (f.required && !String(form[f.name] ?? "").trim()) {
        return toast.error(`${f.label} is required`);
      }
    }
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.name] = parseFloat(payload[f.name] || 0);
    });
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit" : "New"} Record</DialogTitle>
        </DialogHeader>

        {templates && templates.length > 0 && !editing && (
          <div className="mb-3 p-3 rounded-lg border border-pink-100 bg-pink-50/50">
            <Label className="text-xs text-pink-900 mb-1.5 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />{templateLabel || "Load from template"}
            </Label>
            <select data-testid={`${testidPrefix}-template-select`} value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="w-full h-9 rounded-md border border-pink-200 bg-white px-3 text-sm">
              <option value="">— Start from blank —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs text-slate-600 mb-1 block">
                {f.label}{f.required && <span className="text-rose-500"> *</span>}
              </Label>
              <FieldRow f={f} value={form[f.name]} filenameValue={form[`${f.name}_filename`]}
                form={form} setForm={setForm} testidPrefix={testidPrefix}
                uploading={uploading} fileRefs={fileRefs}
                uploadFile={uploadFile} clearFile={clearFile} />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={`${testidPrefix}-save-button`} onClick={save} className="bg-pink-700 hover:bg-pink-800">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
